import { useMemo } from 'react'
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Link } from '@tanstack/react-router'
import { Clock3, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { useDropshipQuoteDrafts, type DropshipQuoteDraftListParams } from '@/api/hooks'
import type { components } from '@/api/schema'
import { cn, formatMoney, formatStatus, parseUTCDate, resultCount } from '@/lib/utils'
import { SearchInput } from '@/components/search-input'
import { ListFilter, ListFilters } from '@/components/list-filters'

type DraftSummary = components['schemas']['DropshipQuoteDraftSummaryRead']
type SortValue = NonNullable<DropshipQuoteDraftListParams['sort']>

const PAGE_SIZE = 20
const ALL_STATUSES = '__all__'

function statusTone(status: string) {
  switch (status) {
    case 'draft':
    case 'validated':
    case 'reviewed':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'needs_review':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'confirm_blocked':
      return 'border-blue-200 bg-blue-50 text-blue-700'
    case 'confirmed':
    case 'project_started':
      return 'border-violet-200 bg-violet-50 text-violet-700'
    case 'cancelled':
      return 'border-muted bg-muted text-muted-foreground'
    default:
      return 'border-muted bg-muted text-muted-foreground'
  }
}

function formatDateTime(value: string) {
  return parseUTCDate(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function relativeCue(value: string) {
  const ageMs = Date.now() - parseUTCDate(value).getTime()
  const hours = Math.max(0, Math.round(ageMs / 36e5))
  if (hours < 1) return 'Updated just now'
  if (hours < 24) return `Updated ${hours}h ago`
  const days = Math.round(hours / 24)
  return `Updated ${days}d ago`
}

function primaryRef(draft: DraftSummary) {
  return (
    draft.job_reference_number?.trim() ||
    draft.source_file_name.replace(/\.aqproj$/i, '').trim() ||
    'Untitled dropship project'
  )
}

function partyLine(draft: DraftSummary) {
  return [draft.customer_name, draft.ship_to_name].filter(Boolean).join(' → ') || 'Customer not parsed'
}

function manufacturerLine(draft: DraftSummary) {
  const names = draft.manufacturer_names.length ? draft.manufacturer_names : draft.vendor_names
  if (!names.length) return 'No manufacturer parsed'
  if (names.length <= 2) return names.join(', ')
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`
}

function DraftStatus({ draft }: { draft: DraftSummary }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant="outline" className={cn('text-xs', statusTone(draft.status))}>
        {formatStatus(draft.status)}
      </Badge>
      {draft.manufacturer_count > 1 && (
        <Badge variant="secondary" className="text-xs">
          {draft.manufacturer_count} makers
        </Badge>
      )}
    </div>
  )
}

function DraftTableSkeleton() {
  return (
    <TableBody>
      {Array.from({ length: 6 }).map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton className="h-12 w-56" /></TableCell>
          <TableCell><Skeleton className="h-10 w-72" /></TableCell>
          <TableCell><Skeleton className="h-8 w-32" /></TableCell>
          <TableCell><Skeleton className="ml-auto h-8 w-24" /></TableCell>
          <TableCell><Skeleton className="h-8 w-28" /></TableCell>
        </TableRow>
      ))}
    </TableBody>
  )
}

function DraftMobileCard({ draft }: { draft: DraftSummary }) {
  return (
    <Link
      to="/projects/dropship-drafts/$draftUuid"
      params={{ draftUuid: draft.uuid }}
      className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-primary">{primaryRef(draft)}</p>
          <p className="truncate text-xs text-muted-foreground">{partyLine(draft)}</p>
        </div>
        <div className="shrink-0 text-right tabular-nums">
          <p className="text-sm font-semibold">{formatMoney(draft.total ?? draft.merchandise_total ?? 0)}</p>
          <p className="text-[11px] text-muted-foreground">{draft.line_count} line{draft.line_count === 1 ? '' : 's'}</p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <DraftStatus draft={draft} />
      </div>
      <div className="mt-2 border-t pt-2 text-xs text-muted-foreground">
        <p className="truncate">{manufacturerLine(draft)}</p>
        <p className="mt-1 flex items-center gap-1"><Clock3 className="h-3 w-3" />{relativeCue(draft.updated_at)}</p>
      </div>
    </Link>
  )
}

export function DropshipDraftListPage() {
  const [search, setSearch] = useUrlSearchText()
  const [filters, setFilters] = useQueryStates({
    status: parseAsString.withDefault(ALL_STATUSES),
    sort: parseAsString.withDefault('recency'),
    page: parseAsInteger.withDefault(1),
  })
  const { status, page } = filters
  const sort = filters.sort as SortValue

  const params = useMemo<DropshipQuoteDraftListParams>(() => ({
    search: search.trim() || undefined,
    status: status === ALL_STATUSES ? undefined : status,
    default_open: status === ALL_STATUSES,
    sort,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [search, status, sort, page])

  const { data, isLoading, error } = useDropshipQuoteDrafts(params)
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dropship Quote Drafts</h1>
          <p className="text-sm text-muted-foreground">
            Open and recent AQProj and from-scratch quote drafts, keyed by job reference number.
          </p>
        </div>
      </div>

      <ListFilters

        count={resultCount(data?.total, 'draft')}
        active={status === ALL_STATUSES ? 0 : 1}
        onClear={() => void setFilters({ status: null, page: 1 })}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              value={search}
              onChange={(event) => { setSearch(event.target.value); void setFilters({ page: 1 }) }}
              className="pl-9"
              placeholder="Search job ref, customer, ship-to, manufacturer, model..."
            />
          </div>
        }
      >
        <ListFilter label="Status">
          <Select value={status} onValueChange={(next) => void setFilters({ status: next, page: 1 })}>
            <SelectTrigger aria-label="Draft status filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES}>Open/recent</SelectItem>
              <SelectItem value="draft,needs_review,validated,confirm_blocked">Open only</SelectItem>
              <SelectItem value="draft">Parsed</SelectItem>
              <SelectItem value="needs_review">Needs review</SelectItem>
              <SelectItem value="validated">Validated</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="project_started">Project started</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="Sort by">
          <Select value={sort} onValueChange={(next) => void setFilters({ sort: next, page: 1 })}>
            <SelectTrigger aria-label="Sort drafts"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recency">Most recent activity</SelectItem>
              <SelectItem value="-updated_at">Updated newest</SelectItem>
              <SelectItem value="updated_at">Updated oldest</SelectItem>
              <SelectItem value="-created_at">Created newest</SelectItem>
              <SelectItem value="created_at">Created oldest</SelectItem>
            </SelectContent>
          </Select>
        </ListFilter>
      </ListFilters>

      {error && (
        <Card className="border-destructive/30">
          <CardContent className="p-4 text-sm text-destructive">Could not load dropship quote drafts.</CardContent>
        </Card>
      )}

      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job reference</TableHead>
              <TableHead>Customer / manufacturer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Totals</TableHead>
              <TableHead>Recency</TableHead>
            </TableRow>
          </TableHeader>
          {isLoading ? <DraftTableSkeleton /> : (
            <TableBody>
              {data?.items?.length ? data.items.map((draft) => (
                <TableRow key={draft.uuid}>
                  <TableCell>
                    <div className="space-y-1">
                      <Link
                        to="/projects/dropship-drafts/$draftUuid"
                        params={{ draftUuid: draft.uuid }}
                        className="font-semibold text-primary hover:underline"
                      >
                        {primaryRef(draft)}
                      </Link>
                      <p className="text-xs text-muted-foreground">Created {formatDateTime(draft.created_at)}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{partyLine(draft)}</p>
                      <p className="text-xs text-muted-foreground">{manufacturerLine(draft)} · {draft.line_count} line{draft.line_count === 1 ? '' : 's'}</p>
                      {draft.ship_to_address && <p className="text-xs text-muted-foreground">{draft.ship_to_address}</p>}
                    </div>
                  </TableCell>
                  <TableCell><DraftStatus draft={draft} /></TableCell>
                  <TableCell className="text-right tabular-nums">
                    <p className="font-semibold">{formatMoney(draft.total ?? draft.merchandise_total ?? 0)}</p>
                    <p className="text-[11px] text-muted-foreground">
                      Ship {formatMoney(draft.freight_total ?? 0)} · Tax {formatMoney(draft.tax_total ?? 0)}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{relativeCue(draft.updated_at)}</p>
                    <p className="text-xs text-muted-foreground">{formatDateTime(draft.updated_at)}</p>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No dropship quote drafts found</TableCell></TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </div>

      <div className="space-y-2 md:hidden">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-32 w-full rounded-lg" />)
        ) : data?.items?.length ? (
          data.items.map((draft) => <DraftMobileCard key={draft.uuid} draft={draft} />)
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">No dropship quote drafts found</p>
        )}
      </div>

      <div className="flex items-center justify-between px-1 py-3">
        <p className="text-sm text-muted-foreground">
          Page {page} of {pages} ({total} total)
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void setFilters({ page: Math.max(1, page - 1) })} disabled={page <= 1}>Previous</Button>
          <Button variant="outline" size="sm" onClick={() => void setFilters({ page: page + 1 })} disabled={page >= pages}>Next</Button>
        </div>
      </div>
    </div>
  )
}
